import { Injectable } from '@angular/core';
import { Repository } from '@clavisco/core';

/**
 * DataStorageService provides methods for managing data storage.
 * It includes functionality to get, set, and remove items from storage, as well as manage temporary in-memory data.
 */
@Injectable({
  providedIn: 'root',
})
export class DataStorageService {
  /**
   * Temporary in-memory data storage.
   */
  private data: any;

  /**
   * Constructs the DataStorageService.
   */
  constructor() {
  }

  /**
   * Retrieves an item from storage by its name.
   *
   * @template T - The type of the item to retrieve.
   * @param {string} itemName - The name of the item to retrieve.
   * @returns {T} The retrieved item cast to the specified type.
   */
  GetItem<T>(itemName: string): T {
    const item = Repository.Behavior.GetStorageObject(itemName);
    return item! as T;
  }

  /**
   * Removes an item from storage by its name.
   *
   * @template T - The type of the item to remove.
   * @param {string} itemName - The name of the item to remove.
   */
  RemoveItem<T>(itemName: string): void {
    const item = Repository.Behavior.GetStorageObject(itemName) as T;
    if (item) {
      Repository.Behavior.DeleteStorageObject(itemName);
    }
  }

  /**
   * Stores an item in storage with the specified name.
   *
   * @template T - The type of the item to store.
   * @param {string} itemName - The name of the item to store.
   * @param {T} item - The item to store.
   */
  SetItem<T>(itemName: string, item: T): void {
    Repository.Behavior.SetStorage(item, itemName);
  }

  /**
   * Sets the temporary in-memory data.
   *
   * @param {any} value - The value to set as in-memory data.
   */
  setData(value: any) {
    this.data = value;
  }

  /**
   * Retrieves the temporary in-memory data.
   *
   * @returns {any} The current in-memory data.
   */
  getData() {
    return this.data;
  }

  /**
   * Removes the temporary in-memory data.
   */
  removeData() {
    this.data = null;
  }
}
